import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ClipboardList, X } from 'lucide-react';
import { SportIndicator } from '@/components/SportIndicator';
import { SkillTree } from '@/components/SkillTree';
import { PositionPaths } from '@/components/PositionPaths';
import { LearningPath } from '@/components/LearningPath';
import { SkillLevelPicker } from '@/components/SkillLevelPicker';
import { WeakAreasCard } from '@/components/WeakAreasCard';
import { useProgress } from '@/hooks/useProgress';
import { learningPaths } from '@/data/lessons';
import { soccerLearningPaths } from '@/data/soccerLessons';
import { basketballLearningPaths } from '@/data/basketballLessons';
import { baseballLearningPaths } from '@/data/baseballLessons';
import { usePreferredSport } from '@/hooks/usePreferredSport';
import { useSportSkillLevel } from '@/hooks/useSportSkillLevel';
import { useAssignedLessons } from '@/hooks/useAssignedLessons';
import { useNewAssignments } from '@/hooks/useNewAssignments';
import { usePremium } from '@/hooks/usePremium';
import { getLessonsForSport } from '@/components/coach/LessonPicker';
import { useAuth } from '@/hooks/useAuth';
import { unlockedSportsKey } from '@/lib/deviceProgressState';

const FREE_SPORT_CAP = 2;

function getUnlockedSports(userId?: string | null): string[] {
  try {
    const raw = localStorage.getItem(unlockedSportsKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function addUnlockedSport(sport: string, userId?: string | null): string[] {
  const current = getUnlockedSports(userId);
  if (current.includes(sport)) return current;
  const next = [...current, sport];
  try { localStorage.setItem(unlockedSportsKey(userId), JSON.stringify(next)); } catch {}
  return next;
}



const SPORTS = [
  { id: 'football', label: 'Football', icon: '🏈', available: true },
  { id: 'soccer', label: 'Soccer', icon: '⚽', available: true },
  { id: 'basketball', label: 'Basketball', icon: '🏀', available: true },
  { id: 'baseball', label: 'Baseball', icon: '⚾', available: true },
];

interface LearnTabProps {
  onSelectLesson: (id: string) => void;
  defaultSport?: string;
}

export function LearnTab({ onSelectLesson, defaultSport = 'football' }: LearnTabProps) {
  const { user } = useAuth();
  const { progress, weakAreas, loaded } = useProgress();
  const { setSport } = usePreferredSport();
  const { getLevel, setLevel, loaded: skillLoaded } = useSportSkillLevel();
  const { isPremium, setShowUpgrade, setUpgradeSportName } = usePremium();
  const assignedLessonIds = useAssignedLessons();
  const { newAssignments, markAllSeen } = useNewAssignments();
  const [activeSport, setActiveSport] = useState(defaultSport);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showLevelPicker, setShowLevelPicker] = useState(false);

  // Ensure the current default sport is tracked as unlocked on mount.
  useEffect(() => {
    addUnlockedSport(defaultSport, user?.id);
  }, [defaultSport, user?.id]);

  useEffect(() => {
    setActiveSport(defaultSport);
    setActiveCategory('all');
  }, [defaultSport]);

  // Show skill level picker when user visits a sport for the first time
  useEffect(() => {
    if (skillLoaded && loaded && !getLevel(activeSport)) {
      const sportInfo = SPORTS.find(s => s.id === activeSport);
      if (sportInfo?.available) {
        setShowLevelPicker(true);
      }
    }
  }, [activeSport, skillLoaded, loaded, getLevel]);

  const handleSportChange = (sportId: string) => {
    // Free tier: cap at FREE_SPORT_CAP unlocked sports.
    if (!isPremium) {
      const unlocked = getUnlockedSports(user?.id);
      if (!unlocked.includes(sportId) && unlocked.length >= FREE_SPORT_CAP) {
        const sportLabel = SPORTS.find(s => s.id === sportId)?.label ?? sportId;
        setUpgradeSportName(sportLabel);
        setShowUpgrade(true);
        return;
      }
      addUnlockedSport(sportId, user?.id);
    }
    setActiveSport(sportId);
    setActiveCategory('all');
    setSport(sportId);
  };


  const handleSkillSelect = async (level: 'beginner' | 'intermediate' | 'advanced') => {
    await setLevel(activeSport, level);
    setShowLevelPicker(false);
  };

  const isFootball = activeSport === 'football';
  const isSoccer = activeSport === 'soccer';
  const isBasketball = activeSport === 'basketball';
  const isBaseball = activeSport === 'baseball';

  const currentPaths = useMemo(() => {
    return isFootball
      ? learningPaths
      : isSoccer
        ? soccerLearningPaths
        : isBasketball
          ? basketballLearningPaths
          : isBaseball
            ? baseballLearningPaths
            : [];
  }, [isFootball, isSoccer, isBasketball, isBaseball]);

  const footballCategories = [
    { id: 'all', label: 'All' },
    { id: 'basics', label: 'Basics', pathIds: ['basics', 'rules'] },
    { id: 'offense', label: 'Offense', pathIds: ['offense', 'advanced-offense'] },
    { id: 'defense', label: 'Defense', pathIds: ['defense'] },
    { id: 'positions', label: 'Positions', pathIds: ['positions'] },
    { id: 'schemes', label: 'Schemes', pathIds: ['schemes-systems'] },
  ];

  const soccerCategories = [
    { id: 'all', label: 'All' },
    { id: 'soccer-basics', label: 'Basics', pathIds: ['soccer-basics'] },
    { id: 'soccer-tactics', label: 'Tactics', pathIds: ['soccer-tactics'] },
    { id: 'soccer-positions', label: 'Positions', pathIds: [] },
  ];

  const basketballCategories = [
    { id: 'all', label: 'All' },
    { id: 'basketball-basics', label: 'Basics', pathIds: ['basketball-basics'] },
    { id: 'basketball-tactics', label: 'Tactics', pathIds: ['basketball-tactics'] },
    { id: 'basketball-positions', label: 'Positions', pathIds: [] },
  ];

  const baseballCategories = [
    { id: 'all', label: 'All' },
    { id: 'baseball-basics', label: 'Basics', pathIds: ['baseball-basics'] },
    { id: 'baseball-hitting-pitching', label: 'Hit/Pitch', pathIds: ['baseball-hitting-pitching'] },
    { id: 'baseball-strategy', label: 'Strategy', pathIds: ['baseball-strategy'] },
    { id: 'baseball-positions', label: 'Positions', pathIds: [] },
  ];

  const allCategories = isFootball
    ? footballCategories
    : isSoccer
      ? soccerCategories
      : isBasketball
        ? basketballCategories
        : isBaseball
          ? baseballCategories
          : [];

  const currentPathIds = new Set(currentPaths.map(p => p.id));
  const categories = allCategories.filter(cat =>
    cat.id === 'all' ||
    cat.id === 'soccer-positions' ||
    cat.id === 'basketball-positions' ||
    cat.id === 'baseball-positions' ||
    (cat as any).pathIds?.some((pid: string) => currentPathIds.has(pid))
  );

  const isPositionsView =
    (isSoccer && activeCategory === 'soccer-positions') ||
    (isBasketball && activeCategory === 'basketball-positions') ||
    (isBaseball && activeCategory === 'baseball-positions');

  const filteredPaths = activeCategory === 'all'
    ? currentPaths
    : isFootball
      ? currentPaths.filter(p => {
          if (activeCategory === 'basics') return p.id === 'basics' || p.id === 'rules';
          if (activeCategory === 'offense') return p.id === 'offense' || p.id === 'advanced-offense';
          if (activeCategory === 'defense') return p.id === 'defense';
          if (activeCategory === 'positions') return p.id === 'positions';
          if (activeCategory === 'schemes') return p.id === 'schemes-systems';
          return p.id === 'situational';
        })
      : isPositionsView
        ? []
        : currentPaths.filter(p => p.id === activeCategory);

  const userSkillLevel = getLevel(activeSport);
  const activeSportInfo = SPORTS.find(s => s.id === activeSport);

  if (!loaded) {
    return (
      <div className="space-y-3 pt-2">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-20 rounded-2xl bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Skill Level Picker Modal */}
      <AnimatePresence>
        {showLevelPicker && activeSportInfo && (
          <SkillLevelPicker
            sport={activeSport}
            sportEmoji={activeSportInfo.icon}
            sportLabel={activeSportInfo.label}
            onSelect={handleSkillSelect}
            onDismiss={() => setShowLevelPicker(false)}
          />
        )}
      </AnimatePresence>

      {/* Sport indicator */}
      <SportIndicator sport={activeSport} />

      {/* New assignment notification banner */}
      <AnimatePresence>
        {newAssignments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            className="overflow-hidden"
          >
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-3 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                <ClipboardList className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm text-foreground">
                  {newAssignments.length} new lesson{newAssignments.length > 1 ? 's' : ''} assigned!
                </p>
                <p className="text-xs text-muted-foreground font-body mt-0.5">
                  Your coach assigned{' '}
                  {(() => {
                    const { allLessons } = getLessonsForSport(activeSport);
                    const names = newAssignments
                      .map(a => allLessons.find(l => l.id === a.lesson_id)?.title)
                      .filter(Boolean)
                      .slice(0, 2);
                    if (names.length === 0) return `${newAssignments.length} lesson${newAssignments.length > 1 ? 's' : ''}`;
                    const suffix = newAssignments.length > 2 ? ` +${newAssignments.length - 2} more` : '';
                    return names.join(', ') + suffix;
                  })()}
                  {newAssignments[0]?.team_name && ` · ${newAssignments[0].team_name}`}
                </p>
              </div>
              <button
                onClick={markAllSeen}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sport selector */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
        {SPORTS.map(sport => (
          <button
            key={sport.id}
            onClick={() => sport.available && handleSportChange(sport.id)}
            className={`whitespace-nowrap px-4 py-2.5 rounded-xl text-sm font-display font-bold border-2 transition-all flex-shrink-0 flex items-center gap-2 ${
              activeSport === sport.id
                ? 'bg-primary text-primary-foreground border-primary'
                : sport.available
                  ? 'bg-card text-foreground border-border hover:border-primary/30'
                  : 'bg-muted/30 text-muted-foreground border-transparent cursor-not-allowed opacity-50'
            }`}
          >
            <span>{sport.icon}</span>
            <span>{sport.label}</span>
            {!sport.available && <Lock className="w-3 h-3" />}
          </button>
        ))}
      </div>

      {!SPORTS.find(s => s.id === activeSport)?.available ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16 space-y-4"
        >
          <div className="text-5xl">{SPORTS.find(s => s.id === activeSport)?.icon || '⚾'}</div>
          <h3 className="font-display font-extrabold text-xl">Coming Soon!</h3>
          <p className="text-sm text-muted-foreground font-body max-w-xs mx-auto">
            We're building this sport's curriculum right now.
          </p>
        </motion.div>
      ) : (
        <>
          {/* My Progress — strengths & gaps moved here from Home */}
          {weakAreas.length > 0 && (
            <section aria-labelledby="my-progress-heading" className="space-y-2">
              <h2
                id="my-progress-heading"
                className="px-1 text-xs font-display font-bold tracking-wider uppercase text-muted-foreground"
              >
                My Progress
              </h2>
              <WeakAreasCard
                weakAreas={weakAreas}
                onPractice={() => setActiveCategory('all')}
              />
            </section>
          )}

          {/* Category filter */}
          {categories.length > 2 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-display font-bold transition-all flex-shrink-0 ${
                    activeCategory === cat.id
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          {/* Lesson path */}
          <motion.div
            key={`${activeSport}-${activeCategory}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {isPositionsView ? (
              <PositionPaths
                completedLessons={progress.completedLessons}
                totalXp={progress.xp}
                onSelectLesson={onSelectLesson}
                sport={activeSport}
              />
            ) : filteredPaths.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <div className="text-4xl">📚</div>
                <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                  No lessons in this category yet.
                </p>
              </div>
            ) : activeCategory === 'all' ? (
              <SkillTree
                paths={filteredPaths}
                completedLessons={progress.completedLessons}
                totalXp={progress.xp}
                onSelectLesson={onSelectLesson}
                userSkillLevel={userSkillLevel}
                assignedLessonIds={assignedLessonIds}
              />
            ) : (
              <LearningPath
                paths={filteredPaths}
                completedLessons={progress.completedLessons}
                totalXp={progress.xp}
                onSelectLesson={onSelectLesson}
                assignedLessonIds={assignedLessonIds}
              />
            )}
          </motion.div>
        </>
      )}
    </div>
  );
}
